import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { FormProvider } from 'react-hook-form';
import { Navigate } from 'react-router';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import GuaranteesRootApplicationSection from '../../components/root/RootApplicationSection.tsx';
import ApplicationHeader from '../../shared/components/ApplicationHeader.tsx';
import ApplicationPageFrame from '../../shared/components/ApplicationPageFrame.tsx';
import InitialDictionariesError from '../../shared/components/InitialDictionariesError/InitialDictionariesError.tsx';
import { guaranteesStepNavigationStyles } from '../../stepNavigation/styles.ts';
import GuaranteesManagerApplicationSidePanelLayout from '../shared/SidePanel/GuaranteesManagerApplicationSidePanelLayout.tsx';
import GuaranteesManagerStepNavigator from '../stepNavigation/GuaranteesManagerStepNavigator.tsx';
import { useManagerApplicationPage } from './useManagerApplicationPage.ts';

const ManagerApplicationPage: React.FC = () => {
  const {
    contragentId,
    currentRouteApplication,
    effectiveApplicationId,
    formMethods,
    headerProps,
    hasInitialDictionariesError,
    isApplicationLoading,
    isInitialDictionariesLoading,
    isRetryingInitialDictionaries,
    modalAlert,
    modalSuccess,
    onModalClose,
    onSuccessModalClose,
    onRetryInitialDictionaries,
    redirectPath,
    rootSectionProps,
    showGuaranteesRequestError,
  } = useManagerApplicationPage();

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  return (
    <ApplicationPageFrame
      modalAlert={modalAlert}
      modalSuccess={modalSuccess}
      onModalClose={onModalClose}
      onSuccessModalClose={onSuccessModalClose}
    >
      <ApplicationHeader {...headerProps} />

      {isApplicationLoading || isInitialDictionariesLoading ? (
        <Box sx={commonStyles.centeredLoader}>
          <CircularProgress />
        </Box>
      ) : hasInitialDictionariesError ? (
        <InitialDictionariesError
          isRetrying={isRetryingInitialDictionaries}
          onRetry={onRetryInitialDictionaries}
        />
      ) : (
        <Box sx={guaranteesClientPageStyles.applicationFlowStack}>
          <GuaranteesManagerApplicationSidePanelLayout
            applicationId={effectiveApplicationId}
            onError={showGuaranteesRequestError}
          >
            <FormProvider {...formMethods}>
              <GuaranteesRootApplicationSection {...rootSectionProps} />
            </FormProvider>
          </GuaranteesManagerApplicationSidePanelLayout>

          <GuaranteesManagerStepNavigator
            application={currentRouteApplication}
            applicationId={effectiveApplicationId}
            contragentId={contragentId}
            currentStepKey="step1"
            containerStyles={guaranteesStepNavigationStyles.compactContainer}
            stepperStyles={guaranteesStepNavigationStyles.compactStepper}
          />
        </Box>
      )}
    </ApplicationPageFrame>
  );
};

export default ManagerApplicationPage;
